import { EmptyState, FlexColumn, FlexRow, LabeledData, Loading, useMoment } from '@gorila-shared-ui/components';
import { Cell, Grid } from 'baseui/layout-grid';
import { Modal, ModalBody, ModalHeader } from 'baseui/modal';
import { LabelSmall } from 'baseui/typography';
import { memo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ONLY_DATE_FORMAT } from '../../../../constants/app';
import { FLEET_JOB_TYPE_LABELS } from '../../../../constants/fleet';
import { useFilesDisplay } from '../../../../hooks/useFilesDisplay';
import { useStyles } from '../../../../hooks/useStyles';
import { FileDisplay } from '../../../../types/app';
import { FleetServiceJob } from '../../../../types/fleet';
import FileThumbnailPreview from '../../../shared/FileThumbnailPreview';
import ImageFullScreenPreviewModal from '../../../shared/ImageFullScreenPreviewModal';

type Props = {
  isOpen: boolean;
  serviceJob: FleetServiceJob;
  onClose: () => void;
};

function FleetServiceJobDetailModal({ serviceJob, onClose, isOpen }: Props) {
  const { css, theme } = useStyles();
  const { formatDateFromTs } = useMoment();
  const [selectedEvidence, setSelectedEvidence] = useState<FileDisplay>();
  const { files: evidences, loading } = useFilesDisplay(serviceJob.evidences);

  return createPortal(
    <Modal
      animate
      closeable
      onClose={() => onClose()}
      isOpen={isOpen}
      size="auto"
      autoFocus={false}
    >
      <FlexRow
        gap={0}
        classNames={css({
          justifyContent: 'space-between',
          margin: '6px 2rem 0px 0px',
        })}
      >
        <ModalHeader
          className={css({
            marginBottom: '0 !important',
          })}
        >
          Detalles del servicio
        </ModalHeader>
      </FlexRow>
      <ModalBody>
        <FlexColumn
          classNames={css({
            width: '70vw',
            maxWidth: '600px',
            minWidth: '350px',
            boxSizing: 'border-box',
            padding: '1rem',
            maxHeight: '80vh',
          })}
        >
          <FlexColumn gap={theme.sizing.scale300}>
            <LabeledData
              label="Tipo:"
              content={FLEET_JOB_TYPE_LABELS[serviceJob.type]}
            />
            <LabeledData
              label="Descripción:"
              content={serviceJob.description || '-'}
              inline={false}
            />
            <LabeledData
              label="Costo:"
              content={`$${serviceJob.cost}`}
            />
            <LabeledData
              label="Fecha:"
              content={formatDateFromTs(serviceJob.date, ONLY_DATE_FORMAT)}
            />
            <LabelSmall>Evidencias:</LabelSmall>
            {evidences && evidences.length > 0 && (
              <FlexColumn
                classNames={css({ overflowY: 'auto' })}
                gap={0}
              >
                <Grid
                  gridColumns={12}
                  gridGaps={[8]}
                  gridGutters={[16]}
                  gridMargins={[0]}
                  behavior="fluid"
                  overrides={{
                    GridWrapper: {
                      style: {
                        width: '100%',
                        margin: 0,
                        overflowX: 'hidden',
                      },
                    },
                  }}
                >
                  {evidences.map((evidence, index) => {
                    if (!evidence.url) return null;
                    return (
                      <Cell
                        span={[12, 12, 12, 12, 6]}
                        key={index}
                      >
                        <FileThumbnailPreview
                          display={evidence}
                          onImageClick={setSelectedEvidence}
                        />
                      </Cell>
                    );
                  })}
                </Grid>
              </FlexColumn>
            )}
            {loading && <Loading />}
            {!loading && !evidences?.length && (
              <EmptyState
                title="No se registraron evidencias."
                style={{ height: '150px' }}
              />
            )}
          </FlexColumn>
        </FlexColumn>
      </ModalBody>
      {selectedEvidence && (
        <ImageFullScreenPreviewModal
          s3Key={selectedEvidence.key}
          source={selectedEvidence.url}
          isOpen
          onClose={() => setSelectedEvidence(undefined)}
        />
      )}
    </Modal>,
    document.body,
  );
}

export default memo(FleetServiceJobDetailModal);
